import React, { useEffect, useState } from 'react';
import { useAolStore } from '../../../context/AolContext';
import { useVesselStore } from '../../../context/VesselContext';
import { AolGroup, ErrorEvent } from '../../../types/ViewModel/AolItem';
import AolContainerComponent from '../aol-container/AolContainer';
import { ErrorEventsWrapper, LeftWrapper, MainPanel, PageContentWrapper, ValidationContainer } from './AolMain.style';
import AolMainHeader from './AolMainHeader';
import AolValidationMismatchComponent from '../aol-validation-mismatch/AolValidationMismatch';
import { PageType } from '../../../types/Page';
import AolListComponent from '../aol-list-component/AolListComponent';
import useGetAolData from '../../../api/aol/useGetAolData';
import { Period } from '../../../enums/PeriodEnum';
import { useUserSettingsStore } from '../../../context/UserSettingsContext';
import { RoleTypeEnum } from '../../../enums/roleTypeEnum';
import { useAuthStore } from '../../../context/AuthContext';
import { AolFilter } from '../../../types/AolFilter';
import { formatAolFromToTimeUrl, formatAolUrlDate, getAolDateFromUrlFormat, getAolGroupDate } from '../../../utils/aol/aolDateUtils';
import { prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';
import { subtractDateDays, getDateNow, isDateBefore, addDateDays, isDateAfter, isDateBetween } from 'odfjell-date-time-utils/dateTimeUtils';
import { QueryParamValueProps, useQueryParamsHandler } from 'odfjell-query-manager';
import useGetBunkeringData from 'src/api/bunkering/useGetBunkeringData';
import { getThreeYearsBefore } from 'src/components/bunkering/BunkeringUtils';
import useApiLoaderHelper from 'src/utils/useApiLoaderHelper';
import { useBunkerContext } from 'src/context/BunkerContext';
import { useStowageStore } from 'src/context/StowageContext';
import useCheckStowageIntegration from 'src/app/useCheckStowageIntegration';
import useGetRotation from 'src/api/useGetRotation';
import { useRotationStore } from 'src/context/RotationContext';

type Props = {
    page: PageType;
};

const AolMainComponent: React.FC<Props> = (props) => {
    const {
        selectedAolGroup,
        selectedAolDataItem,
        aolFilter,
        setAolFilter,
        setDefaultAolFilter,
        defaultAolFilter,
        aolGroups,
        noonReports,
        aolItems,
        aolDataItems,
        period,
        setPeriod,
        setAolListSortAsc,
    } = useAolStore();
    const { selectedVessel } = useVesselStore();

    const { getAolData, getAolDataSinceLastUpdate, getVoyageFuelPrices } = useGetAolData();
    const { getTimeZoneData } = useGetRotation();
    const { timeZoneData } = useRotationStore();
    const { getBunkeringEvents, getBunkerings, getFuelRobData } = useGetBunkeringData();
    const { userSettings, userSettingsLoaded } = useUserSettingsStore();
    const { loadedMasterStowagePlan } = useStowageStore();
    const { role } = useAuthStore();
    const [initialized, setInitialized] = useState<boolean>(false);
    const { getQueryParamValueFromUrl, triggerQueryParamsUpdate } = useQueryParamsHandler();
    const { isLoadingType } = useApiLoaderHelper();
    const isLoadingAppFeatureSettings = isLoadingType('get_app_features');
    const { bunkeringEvents, bunkerings } = useBunkerContext();
    const { checkLoadedStowageIntegration } = useCheckStowageIntegration();
    // ============= USE EFFECTS ================
    useEffect(() => {
        if (loadedMasterStowagePlan && !timeZoneData) {
            const portIds = Array.from(new Set(loadedMasterStowagePlan.rotation.map((portCall) => portCall.portId)));
            if (portIds.length > 0) {
                getTimeZoneData(portIds);
            }
        }
    }, [loadedMasterStowagePlan, timeZoneData]);

    useEffect(() => {
        if (
            !userSettingsLoaded ||
            isLoadingAppFeatureSettings ||
            !userSettings ||
            !selectedVessel ||
            checkLoadedStowageIntegration(selectedVessel.imoNumber, loadedMasterStowagePlan)
        )
            return;
        const aolDateStr = getQueryParamValueFromUrl('aolDate');

        if (!initialized) {
            let newPeriod = Period.FourWeeks;
            if (userSettings?.aolPeriod) newPeriod = Period[userSettings.aolPeriod];
            else if (role?.roleType === RoleTypeEnum.ShipUser) newPeriod = Period.TwoWeeks;

            setPeriod(newPeriod);

            setAolListSortAsc(userSettings.sortAsc);
            let fromDate = subtractDateDays(getDateNow(), newPeriod === Period.TwoWeeks ? 14 : 28);
            let toDate = new Date();

            if (!aolFilter) {
                let newPeriod = Period.FourWeeks;
                if (userSettings?.aolPeriod) newPeriod = Period[userSettings.aolPeriod];
                else if (role?.roleType === RoleTypeEnum.ShipUser) newPeriod = Period.TwoWeeks;
                setPeriod(newPeriod);

                const newFilter: AolFilter = {
                    fromDate,
                    toDate,
                    isDefault: true,
                };
                setAolFilter(newFilter);
                setDefaultAolFilter(newFilter);
            } else {
                fromDate = aolFilter.fromDate;
                toDate = aolFilter.toDate;
            }

            if (!aolDateStr) {
                getAolData(selectedVessel.imoNumber, formatAolUrlDate(fromDate), formatAolUrlDate(toDate));
            }
            setInitialized(true);
            return;
        }

        const ls_userChangedVessel = sessionStorage.getItem('userChangedVessel');

        let fromDateStr = formatAolUrlDate(aolFilter.fromDate);
        let toDateStr = formatAolUrlDate(aolFilter.toDate);
        let hasDefault = false;
        let hasUpdate = false;
        if (!aolGroups || aolGroups.length === 0 || ls_userChangedVessel === 'true') {
            // if aolDateStr exists, but user didnt select a vessel change, assume this is triggered by
            // opening a link directly to an aolDate, set filter range accordingly
            if (aolDateStr && ls_userChangedVessel !== 'true') {
                const aolDateFromQuery = getAolDateFromUrlFormat(aolDateStr);
                const filterFrom = aolFilter.fromDate;
                const maxFromDate = defaultAolFilter.fromDate;
                const maxToDate = defaultAolFilter.toDate;

                if (isDateBefore(aolDateFromQuery, filterFrom, 'day') || isDateBefore(aolDateFromQuery, maxFromDate, 'day')) {
                    fromDateStr = formatAolUrlDate(aolDateFromQuery);
                    toDateStr = formatAolUrlDate(addDateDays(aolDateFromQuery, period === Period.TwoWeeks ? 14 : 28));
                    hasUpdate = true;
                } else if (
                    isDateAfter(aolDateFromQuery, filterFrom, 'day') &&
                    (isDateBetween(aolDateFromQuery, maxFromDate, maxToDate, 'day') || isDateAfter(aolDateFromQuery, maxToDate, 'day'))
                ) {
                    fromDateStr = formatAolUrlDate(maxFromDate);
                    toDateStr = formatAolUrlDate(maxToDate);
                    hasDefault = true;
                    hasUpdate = true;
                }
            }
            // user switched vessel, set filter to default
            if (ls_userChangedVessel === 'true') {
                sessionStorage.setItem('userChangedVessel', 'false');
                hasDefault = true;
                hasUpdate = true;
            }
        }

        if (hasUpdate) {
            setAolFilter({
                fromDate: getAolDateFromUrlFormat(fromDateStr),
                toDate: getAolDateFromUrlFormat(toDateStr),
                isDefault: hasDefault,
            });
        }

        getAolDataSinceLastUpdate(selectedVessel.imoNumber, fromDateStr, toDateStr);
        getBunkeringEvents(selectedVessel.imoNumber, formatAolUrlDate(getThreeYearsBefore(getAolDateFromUrlFormat(fromDateStr))), toDateStr, true);
        getVoyageFuelPrices(selectedVessel.imoNumber);

        if (period === Period.Custom) {
            setPeriod(userSettings?.aolPeriod ?? role?.roleType === RoleTypeEnum.ShipUser ? Period.TwoWeeks : Period.FourWeeks);
        }
    }, [selectedVessel, userSettingsLoaded, userSettings, initialized, isLoadingAppFeatureSettings, loadedMasterStowagePlan]);

    useEffect(() => {
        if (bunkeringEvents && bunkeringEvents.length > 0) {
            getBunkerings(
                selectedVessel.imoNumber,
                formatAolUrlDate(getThreeYearsBefore(aolFilter.fromDate)),
                formatAolUrlDate(aolFilter.toDate),
                false
            );
        }
    }, [bunkeringEvents]);

    useEffect(() => {
        if (bunkerings && bunkerings.length > 0 && aolDataItems && aolDataItems.length > 0) {
            getFuelRobData(
                selectedVessel?.imoNumber,
                aolDataItems.map((item) => item.noonReport?.metadata?.noonReportId).filter((id) => !!id),
                bunkerings.map((bunkering) => bunkering.bunkeringGuid)
            );
        }
    }, [bunkerings, aolDataItems]);

    useEffect(() => {
        if (!aolGroups || aolGroups.length === 0) return;

        const queryParamValues: QueryParamValueProps[] = [];

        const ss_userChangedFilter = sessionStorage.getItem('userChangedFilter');
        // if no aolDate is selected or user changes filter, set selected aolDate to latest in  range
        if (!getQueryParamValueFromUrl('aolDate') || ss_userChangedFilter === 'true') {
            const latestAolGroup: AolGroup = userSettings?.sortAsc
                ? aolGroups.reduce((prev, current) => (isDateAfter(getAolGroupDate(prev), getAolGroupDate(current)) ? prev : current))
                : aolGroups.reduce((prev, current) => (isDateBefore(getAolGroupDate(prev), getAolGroupDate(current)) ? prev : current));

            queryParamValues.push({ name: 'aolDate', newValue: formatAolUrlDate(getAolGroupDate(latestAolGroup)) });
            queryParamValues.push({ name: 'fromTime', newValue: formatAolFromToTimeUrl(latestAolGroup.aolDataItems[0].reportFromTimeLocalTime) });
            queryParamValues.push({ name: 'toTime', newValue: formatAolFromToTimeUrl(latestAolGroup.aolDataItems[0].reportToTimeLocalTime) });

            if (ss_userChangedFilter === 'true') sessionStorage.setItem('userChangedFilter', 'false');
        }

        triggerQueryParamsUpdate(queryParamValues);
    }, [aolGroups]);

    const getEventTypeString = (eventTypeId: number) => {
        switch (eventTypeId) {
            case 1:
                return 'End Of Sea Passage';
            case 3:
                return 'Start Of Sea Passage';
            case 10:
                return 'Well anchored (cargo ops)';
            case 12:
                return 'Anchor aweigh (cargo ops)';
            case 18:
                return 'All fast';
            case 21:
                return 'All clear';
            case 137:
                return 'Dry docking commenced';
            case 138:
                return 'Dry docking completed';
            case 179:
                return 'Well anchored (no cargo)';
            case 181:
                return 'Anchor aweigh (no cargo)';
            case 182:
                return 'Dry docking commenced';
            case 183:
                return 'Dry docking completed';
            case 184:
                return 'Bunker barge alongside';
            case 185:
                return 'Bunker barge casted off';
        }
        return eventTypeId;
    };

    const getPortLogEventError = (errorEvent: ErrorEvent) => {
        const dateString = prettifyDateTimeHHmm(errorEvent.eventDate.actualDateTimeLocal);

        return `${dateString} ${errorEvent.voyageNumber} ${errorEvent.portName} ${errorEvent.berthName}`;
    };

    return (
        <>
            <PageContentWrapper>
                <LeftWrapper>
                    <AolListComponent page={props.page}></AolListComponent>
                </LeftWrapper>
                <MainPanel>
                    {selectedAolDataItem && aolItems && noonReports && selectedAolGroup && (
                        <>
                            <AolMainHeader page={props.page} selectedAolGroup={selectedAolGroup} selectedAolDataItem={selectedAolDataItem} />
                            {selectedVessel && (
                                <>
                                    {selectedAolDataItem.isValid && (
                                        <AolContainerComponent
                                            key={`${selectedAolDataItem?.id}_${selectedAolDataItem?.reportDate}_${selectedVessel.imoNumber}`}
                                            imoNumber={selectedVessel?.imoNumber}
                                            page={props.page}
                                        />
                                    )}
                                    {!selectedAolDataItem.isValid && selectedAolDataItem.aolItem && (
                                        <ValidationContainer>
                                            {selectedAolDataItem.aolItem.portLogAolItem.errorEvents.length === 0 && (
                                                <ErrorEventsWrapper>
                                                    <h3>Can&apos;t determine AOL type</h3>
                                                    <div className="error-row">Could not find any events registered in the last 100 days</div>
                                                </ErrorEventsWrapper>
                                            )}
                                            {selectedAolDataItem.aolItem.portLogAolItem.errorEvents.length > 0 && (
                                                <>
                                                    <ErrorEventsWrapper>
                                                        <h3>Can&apos;t determine AOL type. Please investigate the following reported events:</h3>
                                                        {selectedAolDataItem.aolItem.portLogAolItem.errorEvents.map((ev, i) => (
                                                            <div className="error-row" key={`error_row_${ev.eventId}_${i}`}>
                                                                <span className="event-type">{getEventTypeString(ev.eventTypeId)}</span>
                                                                {getPortLogEventError(ev)}
                                                            </div>
                                                        ))}
                                                    </ErrorEventsWrapper>
                                                    {selectedAolDataItem.aolValidationItems && (
                                                        <AolValidationMismatchComponent aolValidationItems={selectedAolDataItem.aolValidationItems} />
                                                    )}
                                                </>
                                            )}
                                        </ValidationContainer>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </MainPanel>
            </PageContentWrapper>
        </>
    );
};

export default AolMainComponent;
